import { toZonedDateKey } from '@nucleus-commerce/core';

/**
 * Report ranges are calendar days ("YYYY-MM-DD") in the store's timezone, inclusive on both ends.
 * Day arithmetic runs on UTC midnights so it never crosses a DST edge.
 */

export const RANGE_PRESETS = [
  'today',
  'last7',
  'last30',
  'thisMonth',
  'lastMonth',
  'custom',
] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];
export type FixedPreset = Exclude<RangePreset, 'custom'>;

export const DEFAULT_PRESET: FixedPreset = 'last30';
/** Backend limits: a report covers at most 366 days, an on-demand rollup at most 31. */
export const MAX_REPORT_DAYS = 366;
export const MAX_ROLLUP_DAYS = 31;

export const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

export interface DayRange {
  from: string;
  to: string;
}

export interface ResolvedRange extends DayRange {
  preset: RangePreset;
}

export interface RangeSearch {
  range?: RangePreset | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

export type RangeProblem = 'invalid' | 'toBeforeFrom' | 'tooLong';

const parseDay = (day: string) => Date.parse(`${day}T00:00:00Z`);
const formatDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function isDayKey(value: string | undefined): value is string {
  return value !== undefined && DAY_KEY.test(value) && formatDay(parseDay(value)) === value;
}

export function addDays(day: string, days: number): string {
  return formatDay(parseDay(day) + days * DAY_MS);
}

/** Inclusive: a single day counts as 1. */
export function daysInRange({ from, to }: DayRange): number {
  return Math.round((parseDay(to) - parseDay(from)) / DAY_MS) + 1;
}

export function todayIn(timeZone: string, now: Date = new Date()): string {
  return toZonedDateKey(now, timeZone);
}

export function presetRange(preset: FixedPreset, today: string): DayRange {
  const monthStart = `${today.slice(0, 7)}-01`;
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'thisMonth':
      return { from: monthStart, to: today };
    case 'lastMonth': {
      const lastDay = addDays(monthStart, -1);
      return { from: `${lastDay.slice(0, 7)}-01`, to: lastDay };
    }
  }
}

export function rangeProblem(
  from: string | undefined,
  to: string | undefined,
): RangeProblem | null {
  if (!isDayKey(from) || !isDayKey(to)) return 'invalid';
  if (to < from) return 'toBeforeFrom';
  if (daysInRange({ from, to }) > MAX_REPORT_DAYS) return 'tooLong';
  return null;
}

/**
 * The range a URL asks for. A custom range the backend would refuse falls back to the default
 * preset instead of rendering an error page; the picker refuses to write one in the first place.
 */
export function resolveRange(search: RangeSearch, today: string): ResolvedRange {
  const { from, to } = search;
  if (
    search.range === 'custom' &&
    isDayKey(from) &&
    isDayKey(to) &&
    rangeProblem(from, to) === null
  ) {
    return { preset: 'custom', from, to };
  }
  const preset = search.range && search.range !== 'custom' ? search.range : DEFAULT_PRESET;
  return { preset, ...presetRange(preset, today) };
}

/** The backend compares against the period of equal length ending the day before `from`. */
export function previousRange(range: DayRange): DayRange {
  const to = addDays(range.from, -1);
  return { from: addDays(to, -(daysInRange(range) - 1)), to };
}

export function canRollup(range: DayRange): boolean {
  return daysInRange(range) <= MAX_ROLLUP_DAYS;
}
